import React from 'react';
import { motion } from 'framer-motion';
import { GlassCard } from '@/components/ui/glass-card';
import Layout from '@/components/Layout';
import { Mail, Award, Target } from 'lucide-react';

export default function ManagementPage() {
  const managementTeam = [
    {
      name: 'Sushma Singh',
      role: 'Principal',
      qualification: 'B.Sc, B.Ed',
      image: 'images/principalImage.jpeg',
      message: `Dear Students, Parents, and Visitors,\n\nWelcome to our school. Our institution is committed to providing quality education along with strong moral values, discipline, and overall personality development. We believe that education is not only about academic excellence but also about nurturing creativity, confidence, and character in every child.\n\nOur aim is to create a safe, supportive, and inspiring learning environment where students can explore their potential and achieve their dreams. With the dedication of our experienced teachers and the support of parents, we continuously strive to maintain high standards in academics, co-curricular activities, sports, and modern learning practices.`,
      vision: 'Our vision is to create a positive and inspiring learning environment that promotes academic excellence and strong values. We aim to develop confident, responsible, and future-ready students through modern education and holistic growth.'
    },
    {
      name: 'Mahendra Singh',
      role: 'Director',
      qualification: 'Educational Leadership',
      image: 'images/director_img.jpeg',
      message: `Dear Students, Parents, and Visitors,\n\nIt gives me great pleasure to welcome you to our school. Our mission is to provide quality education that nurtures academic excellence, strong values, and overall personality development. We believe that every child has unique potential, and our goal is to provide the right guidance, opportunities, and environment to help them grow with confidence and responsibility.\n\nOur school focuses on modern education methods, skill development, and holistic learning to prepare students for future challenges.`,
      vision: 'Our vision is to build an educational institution that inspires excellence, innovation, and strong moral values. We aim to provide quality education that nurtures creativity, confidence, and leadership skills in every student.'
    },
    {
      name: 'Shivendra Singh',
      role: 'Secretary / Trustee',
      qualification: 'School Administration',
      image: 'images/secretary_img.jpeg',
      message: `Dear Students, Parents, and Visitors,\n\nEducation is the foundation of a progressive and successful society, and our school is committed to providing quality education with strong values and modern learning practices. Our aim is to create a supportive environment where students can grow academically, socially, and morally.\n\nWe continuously strive to enhance educational standards through innovation, discipline, and collaboration between teachers, parents, and management.`,
      vision: 'Our vision is to create a progressive and student-focused educational environment that promotes academic excellence, discipline, and strong moral values. We aim to integrate modern teaching methods with holistic development to prepare students for future challenges.'
    }
  ];

  return (
    <Layout>
      {/* Hero */}
      <section className="py-20 bg-gradient-to-r from-blue-900 via-blue-800 to-blue-950 text-white" data-testid="management-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl md:text-7xl font-bold mb-6">Our Leadership</h1>
            <p className="text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto">
              Meet the dedicated team guiding our school towards excellence
            </p>
          </motion.div>
        </div>
      </section>

      {/* Management Profiles */}
      <section className="py-20 bg-slate-50" data-testid="management-profiles">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-20">
            {managementTeam.map((member, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                viewport={{ once: true }}
              >
                <div className={`grid md:grid-cols-2 gap-12 items-start ${index % 2 === 1 ? 'md:flex-row-reverse' : ''}`}>
                  <div className={index % 2 === 1 ? 'md:order-2' : ''}>
                    <GlassCard className="overflow-hidden">
                      <img
                        src={member.image}
                        alt={member.name}
                        className="w-full h-96 object-cover rounded-xl"
                      />
                      <div className="p-6 text-center">
                        <h2 className="text-3xl font-bold text-blue-900 mb-2">{member.name}</h2>
                        <p className="text-xl text-amber-600 font-semibold mb-2">{member.role}</p>
                        <p className="text-slate-600">{member.qualification}</p>
                      </div>
                    </GlassCard>
                  </div>

                  <div className={index % 2 === 1 ? 'md:order-1' : ''}>
                    <div className="space-y-6">
                      <div>
                        <div className="flex items-center space-x-3 mb-4">
                          <Mail className="w-6 h-6 text-blue-900" />
                          <h3 className="text-2xl font-bold text-blue-900">Message</h3>
                        </div>
                        <GlassCard>
                          <div className="prose prose-slate max-w-none">
                            {member.message.split('\n\n').map((para, i) => (
                              <p key={i} className="text-slate-600 leading-relaxed mb-4">{para}</p>
                            ))}
                          </div>
                          <div className="mt-6 flex items-center space-x-3 pt-6 border-t border-slate-200">
                            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-900 to-blue-700 flex items-center justify-center text-white font-bold text-xl">
                              {member.name.charAt(0)}
                            </div>
                            <div>
                              <p className="font-bold text-blue-900">{member.name}</p>
                              <p className="text-sm text-slate-600">{member.role}</p>
                            </div>
                          </div>
                        </GlassCard>
                      </div>

                      <div>
                        <div className="flex items-center space-x-3 mb-4">
                          <Target className="w-6 h-6 text-amber-600" />
                          <h3 className="text-2xl font-bold text-blue-900">Vision</h3>
                        </div>
                        <GlassCard className="bg-gradient-to-br from-amber-50 to-blue-50">
                          <p className="text-slate-700 leading-relaxed italic">"{member.vision}"</p>
                        </GlassCard>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-gradient-to-br from-blue-900 to-blue-950 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-6">Get in Touch with Our Management</h2>
          <p className="text-blue-100 mb-8">
            For any queries or suggestions, please feel free to contact our office
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:9827780604" className="px-6 py-3 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-xl transition-colors">
              Call: 9827780604
            </a>
            <a href="mailto:nsfschool123@gmail.com" className="px-6 py-3 bg-white text-blue-900 hover:bg-slate-100 font-semibold rounded-xl transition-colors">
              Email: nsfschool123@gmail.com
            </a>
          </div>
        </div>
      </section>
    </Layout>
  );
}